/* AiReviewPopup.js
 *
 * Instrument-panel redesign.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom/client";
import { Box, Paper, Popper } from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import {
  getOutcome,
  getReviewType,
  getConfidenceTier,
  getQualityTier,
  htmlToPlainTextPreserveBlocks,
  truncateText,
} from "../../utils/AiReviewHelpers";
import { buildAiReviewData } from "../../utils/buildAiReviewData";
import {
  AiReviewHeader,
  AiReviewPopupOutcomeSection,
  AiReviewPopupAssignmentSection,
  AiReviewPopupDeepScanSection,
  AiReviewPopupNotesSection,
  AiReviewPopupSummarySection,
  deriveTheme,
} from "./AiReviewSections";

import "./AiReviewPopupLayout.css";
import "./AiReviewSummarySection.css";
import "./AiReviewAssignmentSection.css";
import "./AiReviewNotesSection.css";

let currentInstance = null;

function AiReviewPopupComponent({
  anchorEl,
  row,
  value,
  fieldConfig,
  theme,
  onClose,
  typographyVariant = "comfortable",
  reviews = [],
  reviewers = [],
  config = null,
  viewfieldsInstance = null,
  onAssignSubmission,
}) {
  const [expanded, setExpanded] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [introComplete, setIntroComplete] = useState(false);
  const [assignmentState, setAssignmentState] = useState({ status: "idle", reviewerId: null });
  const [panelLayout, setPanelLayout] = useState({
    availableHeight: 670,
    forceScroll: false,
  });
  const adminClampRef = useRef(null);

  const T = useMemo(() => deriveTheme(theme), [theme]);

  const popupData = useMemo(() => {
    return buildAiReviewData({ row, reviews, reviewers, config, viewfieldsInstance });
  }, [row, reviews, reviewers, config, viewfieldsInstance]);

  const {
    submissionId,
    topRecommendation,
    alternativeRecommendations,
    recommendationError,
    currentSubmissionReviewCount,
    aiReview,
    aiPresentation,
    classification,
  } = popupData;

  useEffect(() => {
    setSelectedRecommendation(topRecommendation || null);
  }, [topRecommendation]);

  useEffect(() => {
    setAssignmentState({ status: "idle", reviewerId: null });
  }, [submissionId, topRecommendation]);

  useEffect(() => {
    setIntroComplete(false);
  }, [row, value, topRecommendation]);

  const hasValue = useMemo(() => aiReview?.hasAnalysis === true, [aiReview]);

  const score = aiReview?.score ?? undefined;
  const confidence = aiReview?.confidenceLevel ?? undefined;
  const specialist = aiReview?.specialistReviewNeeded === true;

  const confTier = useMemo(() => getConfidenceTier(confidence), [confidence]);
  const qualTier = useMemo(() => getQualityTier(score), [score]);
  const outcomeKey = useMemo(() => getOutcome(score, confidence), [score, confidence]);
  const reviewTypeLabel = useMemo(() => getReviewType(score, confidence), [score, confidence]);
  const outcomeLabel = outcomeKey === "ai_holds" ? "Follow AI advice" : "Needs normal review";
  const aiRecommends = outcomeKey === "ai_holds" ? "Single review" : "Full review";

  const structureScore = aiReview?.structureScore ?? null;
  const titleAlignment = aiReview?.titleAlignment ?? null;
  const sectionBreakdown = aiReview?.sectionBreakdown || null;
  const vaguenessFlags = aiReview?.vaguenessFlags || null;
  const structuralSuggestions = Array.isArray(aiReview?.suggestions) ? aiReview.suggestions : [];

  const presentationStrength = aiPresentation?.combined || "";
  const presentationSummary = aiPresentation?.authorSummary || "";
  const presentationNote = aiPresentation?.note || "";
  const presentationSignals = Array.isArray(aiPresentation?.signals) ? aiPresentation.signals : [];
  const recommendedTopic = classification?.topic?.recommendation || null;
  const topicAmbiguity = classification?.topic?.ambiguity || null;
  const recommendedSpeciality = classification?.speciality?.recommendation || null;

  const adminRaw = aiReview?.adminComments || "";
  const reviewerRaw = aiReview?.reviewerComments || "";
  const authorRaw = aiReview?.authorComments || "";
  const adminText = useMemo(() => htmlToPlainTextPreserveBlocks(adminRaw), [adminRaw]);
  const reviewerText = useMemo(() => htmlToPlainTextPreserveBlocks(reviewerRaw), [reviewerRaw]);
  const authorText = useMemo(() => htmlToPlainTextPreserveBlocks(authorRaw), [authorRaw]);
  const adminExcerpt = useMemo(() => truncateText(adminText, 1100), [adminText]);

  const paperWidth = expanded ? 1080 : 720;
  const targetPaperHeight = expanded ? 830 : specialist ? 760 : 670;

  useEffect(() => {
    const updatePanelLayout = () => {
      const viewportHeight = window.innerHeight || 0;
      const minPanelHeight = 320;
      const maxAvailableHeight = Math.max(minPanelHeight, viewportHeight - 24);
      const availableHeight = Math.min(targetPaperHeight, maxAvailableHeight);

      setPanelLayout({
        availableHeight,
        forceScroll: targetPaperHeight > maxAvailableHeight,
      });
    };

    updatePanelLayout();
    window.addEventListener("resize", updatePanelLayout);

    return () => {
      window.removeEventListener("resize", updatePanelLayout);
    };
  }, [targetPaperHeight]);

  const paperHeight = Math.min(targetPaperHeight, panelLayout.availableHeight);
  const useAutoHeight = !expanded && !panelLayout.forceScroll;

  const paperSx = {
    width: `min(${paperWidth}px, calc(100vw - 24px))`,
    height: useAutoHeight ? "auto" : `${paperHeight}px`,
    maxHeight: `${paperHeight}px`,
    transition: "width 220ms cubic-bezier(0.22, 1, 0.36, 1), height 220ms cubic-bezier(0.22, 1, 0.36, 1)",
    backgroundColor: T.panelBg,
    border: `1.5px solid ${T.borderMedium}`,
    boxShadow: `0 18px 36px rgba(0,0,0,0.34), 0 6px 14px rgba(0,0,0,0.2), 0 0 0 1px ${T.borderMedium}`,
    borderRadius: "16px",
    overflow: "visible",
  };

  if (!hasValue) return null;

  const getRecommendationId = (rec) =>
    rec?.reviewerId ||
    rec?.reviewer?.id ||
    rec?.reviewer?.userId ||
    rec?.reviewer?.email ||
    null;

  const recommendationPool = [
    topRecommendation,
    ...(Array.isArray(alternativeRecommendations) ? alternativeRecommendations : []),
  ].filter(Boolean);

  const distinctRecommendations = recommendationPool.filter((rec, index, arr) => {
    const id = getRecommendationId(rec);
    if (!id) return true;
    return index === arr.findIndex((candidate) => getRecommendationId(candidate) === id);
  });

  const alternativePool = distinctRecommendations.filter((rec) => {
    if (!selectedRecommendation) return true;
    return getRecommendationId(rec) !== getRecommendationId(selectedRecommendation);
  });

  const cssVars = {
    "--aiAccent": T.brand,
    "--aiAccent2": T.brandLight,
    "--aiBrandLight": T.brandLight,
    "--aiReview": T.review,
    "--aiWidget": T.widget,
    "--aiGold": T.gold,
    "--aiSubmit": T.submission,
    "--tierHigh": T.accepted,
    "--tierMed": "#f59e0b",
    "--tierLow": T.rejected,
    "--panelBg": T.panelBg,
    "--surface1": T.surface1,
    "--surface2": T.surface2,
    "--borderSubtle": T.borderSubtle,
    "--borderMedium": T.borderMedium,
    "--textPrimary": T.textPrimary,
    "--textSecondary": T.textSecondary,
    "--textMuted": T.textMuted,
    "--fontFamily": T.fontFamily,
  };

  const handleSelectRecommendation = (recommendation) => {
    setSelectedRecommendation(recommendation);
  };

  const handleAssignRecommendation = async (reviewerCard) => {
    const reviewerId = reviewerCard?.reviewerId ?? null;

    if (!submissionId || !reviewerId || typeof onAssignSubmission !== "function") {
      return;
    }

    setAssignmentState({ status: "assigning", reviewerId });

    try {
      const result = await onAssignSubmission({
        submissionId,
        reviewerIds: [reviewerId],
        aiAssignmentApprovedByAdmin: true,
      });

      if (result?.skipped) {
        setAssignmentState({ status: "idle", reviewerId: null });
        return result;
      }

      setAssignmentState({ status: "assigned", reviewerId });
      return result;
    } catch (error) {
      setAssignmentState({ status: "idle", reviewerId: null });
      throw error;
    }
  };

  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement="right"
      strategy="fixed"
      modifiers={[
        { name: "offset", options: { offset: [10, 0] } },
        { name: "flip", options: { fallbackPlacements: ["left", "right-start", "left-start"], padding: 12 } },
        { name: "preventOverflow", options: { boundary: "viewport", padding: 12, altAxis: true, tether: false } },
      ]}
      className="ai-review-popup__popper"
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          className={`ai-review-popup__paper ${expanded ? "is-expanded" : ""}`}
          sx={paperSx}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose?.();
            }}
            type="button"
            aria-label="Close AI review panel"
            style={{
              position: "absolute",
              top: -18,
              right: -18,
              width: 48,
              height: 48,
              borderRadius: 16,
              border: `1px solid ${T.borderMedium}`,
              background: `linear-gradient(180deg, ${T.surface2} 0%, ${T.panelBg} 100%)`,
              color: T.textPrimary,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              boxShadow: `0 16px 26px rgba(0,0,0,0.24), inset 0 4px 7px rgba(255,255,255,0.10), inset 0 -9px 14px rgba(0,0,0,0.18)`,
              zIndex: 4,
            }}
          >
            <span
              style={{
                position: "absolute",
                inset: 3,
                borderRadius: 13,
                border: `1px solid ${T.brandLight}14`,
                boxShadow: "inset 0 2px 0 rgba(255,255,255,0.07)",
                pointerEvents: "none",
              }}
            />
            <span
              style={{
                position: "absolute",
                top: 6,
                left: 8,
                right: 8,
                height: 14,
                borderRadius: 999,
                background: "linear-gradient(180deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.02) 100%)",
                pointerEvents: "none",
              }}
            />
            <span
              style={{
                position: "absolute",
                left: 7,
                top: 8,
                bottom: 8,
                width: 12,
                borderRadius: 999,
                background: "linear-gradient(90deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.01) 100%)",
                pointerEvents: "none",
              }}
            />
            <span
              style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                zIndex: 1,
                background: "rgba(0,0,0,0.08)",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </span>
          </button>

          <Box
            className={`ai-review-popup__inner tv-${typographyVariant}`}
            sx={cssVars}
            style={{ display: "flex", flexDirection: "column", height: useAutoHeight ? "auto" : "100%", borderRadius: 16, overflow: "hidden" }}
          >
            <AiReviewHeader score={score} expanded={expanded} setExpanded={setExpanded} onClose={onClose} T={T} />

            <Box className="ai-review-popup__contentScroll" style={{ flex: useAutoHeight ? "0 0 auto" : 1, overflowY: expanded || panelLayout.forceScroll ? "auto" : "hidden", display: "flex", flexDirection: "column", gap: 12, padding: "8px 0 12px", minHeight: 0 }}>
              <AiReviewPopupSummarySection
                T={T}
                score={score}
                confTier={confTier}
                qualTier={qualTier}
                outcomeKey={outcomeKey}
                outcomeLabel={outcomeLabel}
                reviewTypeLabel={reviewTypeLabel}
                aiRecommends={aiRecommends}
                specialist={specialist}
                currentSubmissionReviewCount={currentSubmissionReviewCount}
                presentationStrength={presentationStrength}
                presentationSummary={presentationSummary}
                presentationNote={presentationNote}
                presentationSignals={presentationSignals}
                onIntroComplete={setIntroComplete}
              />

              <AiReviewPopupOutcomeSection
                T={T}
                specialist={specialist}
                aiRecommends={aiRecommends}
                cycleStartedAt={`${submissionId || row?.id || ""}-${value || ""}`}
              />

              {introComplete && (
                <>
                  <AiReviewPopupNotesSection
                    T={T}
                    expanded={expanded}
                    adminClampRef={adminClampRef}
                    adminExcerpt={adminExcerpt}
                    adminText={adminText}
                    reviewerText=""
                    authorText=""
                    includeReviewer={false}
                    includeAuthor={false}
                  />

                  <AiReviewPopupAssignmentSection
                    T={T}
                    expanded={expanded}
                    recommendationError={recommendationError}
                    selectedRecommendation={selectedRecommendation}
                    alternativeRecommendations={alternativePool}
                    onSelectRecommendation={handleSelectRecommendation}
                    onAssignRecommendation={handleAssignRecommendation}
                    assignmentState={assignmentState}
                    specialist={specialist}
                  />

                  {expanded && (
                    <>
                      <AiReviewPopupNotesSection
                        T={T}
                        expanded={expanded}
                        adminClampRef={adminClampRef}
                        adminExcerpt={adminExcerpt}
                        adminText=""
                        reviewerText={reviewerText}
                        authorText={authorText}
                        includeAdmin={false}
                      />

                      <div style={{ padding: "0 16px", margin: 0 }}>
                        <AiReviewPopupDeepScanSection
                          T={T}
                          score={score}
                          specialist={specialist}
                          structureScore={structureScore}
                          titleAlignment={titleAlignment}
                          sectionBreakdown={sectionBreakdown}
                          vaguenessFlags={vaguenessFlags}
                          structuralSuggestions={structuralSuggestions}
                          recommendedTopic={recommendedTopic}
                          topicAmbiguity={topicAmbiguity}
                          recommendedSpeciality={recommendedSpeciality}
                          presentationStrength={presentationStrength}
                          presentationSummary={presentationSummary}
                          currentSubmissionReviewCount={currentSubmissionReviewCount}
                        />
                      </div>
                    </>
                  )}
                </>
              )}

              {!introComplete && (
                <Box
                  style={{
                    flex: 1,
                    minHeight: 0,
                    margin: "-1px 1px 1px",
                    background: T.panelBg,
                    position: "relative",
                    zIndex: 2,
                  }}
                />
              )}
            </Box>
          </Box>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
}

function open(props) {
  if (currentInstance) {
    currentInstance.close();
  }
  const container = document.createElement("div");
  document.body.appendChild(container);
  const root = ReactDOM.createRoot(container);
  const close = () => {
    root.unmount();
    container.remove();
    currentInstance = null;
  };
  root.render(<AiReviewPopupComponent {...props} onClose={close} />);
  currentInstance = { close };
}

export const AiReviewPopup = { open };
